/**
 * Infectious particles calculator.
 *
 * Vial math uses exact decimal arithmetic (BigInt-based digits/scale pairs),
 * so the recommended quantity is always an exact whole integer — even for
 * scientific-notation targets far beyond Number.MAX_SAFE_INTEGER such as
 * 1e75, where float Math.ceil used to display values like
 * 2.3696682464454976e+68 instead of a true integer.
 */

const AVIPCMath = {
  // Conservative cart ceiling. Recommendations above it are displayed
  // exactly but are never written into the product quantity input or
  // submitted to the cart, which cannot safely represent them.
  cartMaxQuantity: 100000n,

  // Targets beyond this exponent magnitude (either direction) are invalid,
  // preserving the old Number-based overflow/underflow rejection.
  maxInputExponent: 1000,

  superscriptCharacters: {
    '⁰': '0',
    '¹': '1',
    '²': '2',
    '³': '3',
    '⁴': '4',
    '⁵': '5',
    '⁶': '6',
    '⁷': '7',
    '⁸': '8',
    '⁹': '9',
    '⁺': '+',
    '⁻': '-',
  },

  normalizeSuperscriptCharacters(value) {
    return Array.from(String(value), (character) => this.superscriptCharacters[character] ?? character).join('');
  },

  /**
   * Parse an unsigned decimal string like "4.22" into an exact decimal
   * { digits: BigInt, scale } representing digits / 10^scale.
   * Returns null when malformed or zero.
   */
  parseUnsignedDecimal(text) {
    const match = /^([0-9]*)(?:\.([0-9]+))?$/.exec(String(text).trim());
    if (!match) return null;

    const integerPart = match[1] ?? '';
    const fractionPart = match[2] ?? '';
    if (!integerPart && !fractionPart) return null;

    const digits = BigInt(`${integerPart || '0'}${fractionPart}`);
    if (digits === 0n) return null;

    return { digits, scale: fractionPart.length };
  },

  /**
   * Parse a customer-entered target: an unsigned decimal, optionally with
   * an e/E, x10^, ×10^, or superscript exponent. Commas and whitespace are
   * ignored. Returns an exact decimal { digits: BigInt, scale } or null for
   * malformed, zero, or negative input.
   */
  parseTargetInput(value) {
    const normalized = this.normalizeSuperscriptCharacters(String(value))
      .trim()
      .replace(/,/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, '');

    if (normalized.startsWith('-')) return null;

    const match = /^([0-9]*\.?[0-9]+)(?:(?:x10\^?|e)([+-]?[0-9]+))?$/i.exec(normalized);
    if (!match) return null;

    const decimal = this.parseUnsignedDecimal(match[1]);
    if (!decimal) return null;

    if (match[2] !== undefined) {
      const exponent = Number(match[2]);
      if (!Number.isInteger(exponent) || Math.abs(exponent) > this.maxInputExponent) return null;
      return this.applyExponent(decimal, BigInt(exponent));
    }

    return decimal;
  },

  /** Multiply an exact decimal by 10^exponent, keeping scale non-negative. */
  applyExponent(decimal, exponent) {
    let { digits, scale } = decimal;
    scale -= Number(exponent);

    if (scale < 0) {
      digits *= 10n ** BigInt(-scale);
      scale = 0;
    } else {
      while (scale > 0 && digits % 10n === 0n) {
        digits /= 10n;
        scale -= 1;
      }
    }

    return { digits, scale };
  },

  /** Exact decimal multiplication: (a.digits / 10^a.scale) × (b.digits / 10^b.scale). */
  multiplyDecimals(a, b) {
    return { digits: a.digits * b.digits, scale: a.scale + b.scale };
  },

  /**
   * Exact ceil(numerator / denominator) for positive exact decimals:
   * ceil((n.digits / 10^n.scale) / (d.digits / 10^d.scale)) as a BigInt,
   * with no float rounding at any magnitude. Returns null if either side is zero.
   */
  ceilDivideDecimals(numerator, denominator) {
    if (numerator.digits === 0n || denominator.digits === 0n) return null;

    let scaledNumerator;
    let scaledDenominator;
    if (numerator.scale >= denominator.scale) {
      // ratio = n.digits / (d.digits * 10^(n.scale - d.scale))
      scaledNumerator = numerator.digits;
      scaledDenominator = denominator.digits * 10n ** BigInt(numerator.scale - denominator.scale);
    } else {
      // ratio = (n.digits * 10^(d.scale - n.scale)) / d.digits
      scaledNumerator = numerator.digits * 10n ** BigInt(denominator.scale - numerator.scale);
      scaledDenominator = denominator.digits;
    }

    return (scaledNumerator + scaledDenominator - 1n) / scaledDenominator;
  },

  /** Exact grouped integer, e.g. 1234567n → "1,234,567". Never e-notation, never a fraction. */
  formatInteger(value) {
    const digits = String(value);
    const groups = [];
    for (let end = digits.length; end > 0; end -= 3) {
      groups.push(digits.slice(Math.max(0, end - 3), end));
    }
    return groups.reverse().join(',');
  },

  /**
   * Cart-safe quantity string for a recommendation, or null when it exceeds
   * cartMaxQuantity. Astronomical values must never reach the quantity
   * input or the add-to-cart submission.
   */
  toCartQuantityString(value) {
    if (typeof value !== 'bigint' || value <= 0n || value > this.cartMaxQuantity) return null;
    return String(value);
  },

  parseExponent(value) {
    return Number(this.normalizeSuperscriptCharacters(String(value)));
  },

  decimalToNumber(decimal) {
    return Number(`${decimal.digits}e${-decimal.scale}`);
  },

  /**
   * Parse the product description for titer, unit, and vial volume.
   * Returns exact decimals (titerDecimal, volumeDecimal, perVial) plus
   * display-friendly Numbers (titer, vialVolume), or null when unusable.
   */
  parseProductDescription(text) {
    const normalized = String(text).replace(/\u00a0/g, ' ').replace(/,/g, ' ');
    const titerPattern = /Titer\s*:\s*([0-9]+(?:\.[0-9]+)?)\s*(?:x|×)\s*10\s*(?:\^\s*)?([+-]?\d{1,3})\s*(PFU|TCID50|CCID50)\s*\/\s*mL/i;
    const eNotationPattern = /Titer\s*:\s*([0-9]+(?:\.[0-9]+)?)\s*e\s*([+-]?\d{1,3})\s*(PFU|TCID50|CCID50)\s*\/\s*mL/i;
    const superscriptPattern = /Titer\s*:\s*([0-9]+(?:\.[0-9]+)?)\s*(?:x|×)\s*10\s*([⁺⁻]?[⁰¹²³⁴⁵⁶⁷⁸⁹]{1,3})\s*(PFU|TCID50|CCID50)\s*\/\s*mL/i;
    const plainPattern = /Titer\s*:\s*([0-9]+(?:\.[0-9]+)?)\s*(PFU|TCID50|CCID50)\s*\/\s*mL/i;
    const volumePattern = /\bFormat(?:\s+Options)?\s*:[\s\S]{0,240}?([0-9]+(?:\.[0-9]+)?)\s*mL\s*\/\s*vial/i;

    const scientificMatch = normalized.match(titerPattern)
      || normalized.match(eNotationPattern)
      || normalized.match(superscriptPattern);
    const plainMatch = normalized.match(plainPattern);
    const volumeMatch = normalized.match(volumePattern);

    if ((!scientificMatch && !plainMatch) || !volumeMatch) return null;

    const titerCoefficient = this.parseUnsignedDecimal(scientificMatch ? scientificMatch[1] : plainMatch[1]);
    const volumeDecimal = this.parseUnsignedDecimal(volumeMatch[1]);

    if (!titerCoefficient || !volumeDecimal) return null;

    const titerDecimal = scientificMatch
      ? this.applyExponent(titerCoefficient, BigInt(this.parseExponent(scientificMatch[2])))
      : titerCoefficient;

    const titer = this.decimalToNumber(titerDecimal);
    const vialVolume = this.decimalToNumber(volumeDecimal);

    if (!Number.isFinite(titer) || titer <= 0 || !Number.isFinite(vialVolume) || vialVolume <= 0) {
      return null;
    }

    return {
      titerDecimal,
      volumeDecimal,
      perVial: this.multiplyDecimals(titerDecimal, volumeDecimal),
      titer,
      vialVolume,
      unit: (scientificMatch ? scientificMatch[3] : plainMatch[2]).toUpperCase(),
    };
  },
};

// Exact math helpers are exported for the Node test suite; browsers never define `module`.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { AVIPCMath };
}

if (typeof customElements !== 'undefined' && !customElements.get('infectious-particles-calculator')) {
  class InfectiousParticlesCalculator extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('.av-ipc__source')?.textContent || '';
      const productData = AVIPCMath.parseProductDescription(source);

      if (!productData) return;

      this.productData = productData;
      this.targetInput = this.querySelector('[data-ipc-target]');
      this.resultOutput = this.querySelector('[data-ipc-result]');
      this.addButton = this.querySelector('[data-ipc-add]');
      this.limitNote = this.querySelector('[data-ipc-limit]');

      if (!this.targetInput || !this.resultOutput || !this.addButton) return;

      const configuredTarget = this.dataset.defaultTarget;
      if (configuredTarget && Number(configuredTarget) > 0) {
        this.targetInput.value = this.formatInputValue(Number(configuredTarget));
      }

      this.querySelector('[data-ipc-unit]').textContent = 'infectious particles';
      this.querySelector('[data-ipc-product-data]').textContent =
        `${this.formatScientific(productData.titer)} infectious particles/mL • ${this.formatNumber(productData.vialVolume)} mL/vial`;

      this.targetInput.addEventListener('input', () => this.update());
      this.addButton.addEventListener('click', () => this.addRecommendedQuantity());
      this.hidden = false;
      this.update();
    }

    parseTarget(value) {
      return AVIPCMath.parseTargetInput(value);
    }

    update() {
      if (this.limitNote) this.limitNote.hidden = true;

      const target = this.parseTarget(this.targetInput.value);

      if (!target) {
        this.recommendedQuantity = null;
        this.resultOutput.dataset.invalid = 'true';
        delete this.resultOutput.dataset.long;
        this.resultOutput.textContent = 'Enter a valid amount';
        this.addButton.disabled = true;
        return;
      }

      // Exact upward rounding: always a whole integer at any magnitude.
      this.recommendedQuantity = AVIPCMath.ceilDivideDecimals(target, this.productData.perVial);

      if (!this.recommendedQuantity) {
        this.resultOutput.dataset.invalid = 'true';
        this.resultOutput.textContent = 'Enter a valid amount';
        this.addButton.disabled = true;
        return;
      }

      delete this.resultOutput.dataset.invalid;

      const formatted = AVIPCMath.formatInteger(this.recommendedQuantity);
      if (formatted.replace(/,/g, '').length > 15) this.resultOutput.dataset.long = 'true';
      else delete this.resultOutput.dataset.long;
      this.resultOutput.textContent = `${formatted} ${this.recommendedQuantity === 1n ? 'vial' : 'vials'}`;

      if (!AVIPCMath.toCartQuantityString(this.recommendedQuantity)) {
        // Far beyond anything the cart can represent: keep showing the exact
        // count, but never write it into the quantity input or click add.
        this.addButton.disabled = true;
        if (this.limitNote) this.limitNote.hidden = false;
        return;
      }

      this.addButton.disabled = false;
    }

    addRecommendedQuantity() {
      const quantity = AVIPCMath.toCartQuantityString(this.recommendedQuantity);
      if (!quantity) return;

      const productInfo = this.closest('.product-info');
      const form = productInfo?.querySelector('.js-product-form-main');
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const addButton = form?.querySelector('button[name="add"]');

      if (!form || !quantityInput || !addButton || addButton.disabled) return;

      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      addButton.click();
    }

    formatScientific(value) {
      const [coefficient, exponent] = value.toExponential(2).split('e');
      return `${Number(coefficient)} × 10^${Number(exponent)}`;
    }

    formatInputValue(value) {
      return value.toExponential(0).replace('+', '');
    }

    formatNumber(value) {
      return Number.isInteger(value) ? value.toFixed(1) : String(value);
    }
  }

  customElements.define('infectious-particles-calculator', InfectiousParticlesCalculator);
}
